import { useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import useSWR from 'swr';
import { getQueriesForClient } from '~/app/_repositories/query-service';
import { useTellerSession } from '~/common/hooks/use-auth/use-teller-session';
import {
  addComment as addCommentAction,
  replyComment as replyCommentAction,
} from '@app/_repositories/comment-service';
import type { PostCommentContainerProps, PostCommentFormData } from './type';

type Params = PostCommentContainerProps;

export const useLocalState = ({
  storyId,
  replyComment,
  onBeforePost,
  onPostSuccess,
  onPostError,
}: Params) => {
  // Comment Post
  const sessionUser = useTellerSession();
  const isLogin =
    sessionUser.session && sessionUser.session.isAnonymous === false;
  const tellerUser = sessionUser.session?.tellerUser;

  // can comment
  const { data: canAddComment } = useSWR(
    isLogin ? [`can-comment-${storyId}`, storyId] : null,
    async ([key, storyId]) => {
      const response = await getQueriesForClient({
        request_list: [
          {
            request_id: key,
            story_request: {
              id: storyId,
              require_story: {
                require_comment_permission: {
                  require_can_add: true,
                },
              },
            },
          },
        ],
      });

      return (
        response.response_list.find((res) => res.request_id === key)?.story
          ?.comment_permission?.can_add ?? false
      );
    }
  );

  // form
  const form = useForm<PostCommentFormData>({
    defaultValues: {
      commentText: '',
      replyCommentId: replyComment?.id ?? undefined,
    },
  });
  const { setFocus, setValue } = form;

  // set focus when replyCommentId is set
  useEffect(() => {
    setValue('replyCommentId', replyComment?.id ?? undefined);
    if (replyComment) {
      setFocus('commentText');
    }
  }, [replyComment, setValue, setFocus]);

  const onSubmit: SubmitHandler<PostCommentFormData> = useCallback(
    async (data) => {
      try {
        onBeforePost?.(data);
        const addCommentRequest = {
          story_id: storyId,
          stamp_id: undefined,
          body: data.commentText,
        };

        await (data.replyCommentId
          ? replyCommentAction({
              parent_comment_id: data.replyCommentId,
              add_request: addCommentRequest,
            })
          : addCommentAction(addCommentRequest));

        form.reset();

        onPostSuccess?.(data);
      } catch (error) {
        if (error instanceof Error) {
          onPostError?.(error);
        }
      }
    },
    [form, onBeforePost, onPostError, onPostSuccess, storyId]
  );

  return {
    isLogin,
    tellerUser,
    canAddComment,
    form,
    onSubmit,
  };
};
